<template>
    <span ref="containerRef">
        <slot name="trigger" v-if="!triggerTarget" />
        <transition
            enter-active-class="transition-opacity ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity ease-in duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            @after-leave="onAfterLeave"
            @enter="onEnter"
        >
            <span
                :class="[className, 'z-30']"
                :style="popoverStyle"
                ref="popoverRef"
                @mouseenter="onPopoverMouseEnter"
                @mouseleave="onPopoverMouseLeave"
                v-show="show"
            >
                <slot />
            </span>
        </transition>
    </span>
</template>

<script setup lang="ts">
    import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick, useSlots } from 'vue';
    import { createPopper, Modifier, Instance as PopperInstance } from '@popperjs/core';
    import { onClickOutside } from '@vueuse/core';
    import {
        PopoverPlacement,
        PopoverTrigger,
        PopoverStrategy,
    } from '@/components/activix-ui/ActivixPopover/types.ts';

    export type ActivixPopoverType = {
        open: () => void;
        close: () => void;
        toggle: () => void;
    };

    interface Props {
        triggerTarget?: HTMLElement | null;
        event?: PopoverTrigger;
        sameWidth?: boolean;
        strategy?: PopoverStrategy;
        placement?: PopoverPlacement;
        className?: string;
        popoverStyle?: Record<string, string | number>;
        closeDelay?: number;
        offset?: number;
    }

    const props = withDefaults(defineProps<Props>(), {
        triggerTarget: null,
        event: PopoverTrigger.Click,
        sameWidth: false,
        strategy: PopoverStrategy.Fixed,
        placement: PopoverPlacement.Top,
        className: 'bg-white rounded-md shadow-lg border border-gray-200',
        popoverStyle: undefined,
        closeDelay: 100,
        offset: 4,
    });

    const emit = defineEmits<{
        (e: 'enter'): void;
    }>();

    const containerRef = ref<HTMLElement | null>(null);
    const popoverRef = ref<HTMLElement | null>(null);
    const slots = useSlots();

    const show = ref(false);
    const hoveringTrigger = ref(false);
    const hoveringPopover = ref(false);
    let popper: PopperInstance | null = null;

    const triggerEl = computed(() => {
        if (props.triggerTarget) {
            return props.triggerTarget;
        }
        return slots.trigger
            ? (containerRef.value?.children[0] as HTMLElement | undefined)
            : undefined;
    });

    function initPopper() {
        if (!triggerEl.value || !popoverRef.value || popper) {
            return;
        }

        const modifiers: Partial<Modifier<any, any>>[] = [
            {
                name: 'offset',
                options: {
                    offset: [0, props.offset],
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    boundary: triggerEl.value.closest('main') ?? document.body,
                },
            },
        ];

        if (props.sameWidth) {
            modifiers.push({
                name: 'sameWidth',
                enabled: true,
                phase: 'beforeWrite',
                requires: ['computeStyles'],
                fn: ({ state }) => {
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                },
                effect: ({ state }) => {
                    // @ts-expect-error - we know that the reference is an element
                    state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
                },
            });
        }

        popper = createPopper(triggerEl.value, popoverRef.value, {
            placement: props.placement as PopoverPlacement,
            strategy: props.strategy as PopoverStrategy,
            modifiers,
        });
    }

    function destroyPopper() {
        if (popper) {
            popper.destroy();
            popper = null;
        }
    }

    function toggle() {
        if (show.value) {
            close();
        } else {
            open();
        }
    }

    function open() {
        show.value = true;
    }

    function close() {
        show.value = false;
    }

    function shouldClose() {
        setTimeout(() => {
            if (!hoveringTrigger.value && !hoveringPopover.value) {
                close();
            }
        }, props.closeDelay);
    }

    function onEnter() {
        initPopper();
        emit('enter');
    }

    function onAfterLeave() {
        destroyPopper();
    }

    function onTriggerClick() {
        toggle();
    }

    function onTriggerMouseEnter() {
        hoveringTrigger.value = true;
        open();
    }

    function onTriggerMouseLeave() {
        hoveringTrigger.value = false;
        shouldClose();
    }

    function onPopoverMouseEnter() {
        // Only track hover state for hover triggers. For click triggers, we don't want
        // accidental mouse movements to hide the popover.
        if (props.event !== PopoverTrigger.Hover) return;
        hoveringPopover.value = true;
    }

    function onPopoverMouseLeave() {
        // Only auto-close on mouseleave for hover triggers. Click-triggered popovers
        // should remain open until explicitly closed (click outside or programmatically).
        if (props.event !== PopoverTrigger.Hover) return;
        hoveringPopover.value = false;
        shouldClose();
    }

    function addListeners() {
        if (!triggerEl.value) return;

        if (props.event === PopoverTrigger.Click) {
            triggerEl.value.addEventListener('click', onTriggerClick);
        }

        if (props.event === PopoverTrigger.Hover) {
            triggerEl.value.addEventListener('mouseenter', onTriggerMouseEnter);
            triggerEl.value.addEventListener('mouseleave', onTriggerMouseLeave);
        }
    }

    function removeListeners() {
        if (!triggerEl.value) return;

        triggerEl.value.removeEventListener('click', onTriggerClick);
        triggerEl.value.removeEventListener('mouseenter', onTriggerMouseEnter);
        triggerEl.value.removeEventListener('mouseleave', onTriggerMouseLeave);
    }

    onClickOutside(containerRef, (event) => {
        if (triggerEl.value?.contains(event.target as Node)) return;
        close();
    });

    onMounted(() => {
        addListeners();
    });

    onBeforeUnmount(() => {
        removeListeners();
        destroyPopper();
    });

    watch(triggerEl, (newVal, oldVal) => {
        if (oldVal) {
            oldVal.removeEventListener('click', onTriggerClick);
            oldVal.removeEventListener('mouseenter', onTriggerMouseEnter);
            oldVal.removeEventListener('mouseleave', onTriggerMouseLeave);
        }

        hoveringTrigger.value = false;
        hoveringPopover.value = false;

        const wasOpen = show.value;
        destroyPopper();

        if (newVal) {
            addListeners();
        }

        if (wasOpen && newVal) {
            nextTick(initPopper);
        }
    });

    defineExpose<ActivixPopoverType>({
        toggle,
        open,
        close,
    });
</script>
